import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Building2, Globe2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SERVICE_META } from "../i18n/translations";
import { ICONS, SectionTag, Reveal } from "../components/shared";
import { IMAGES, OFFICE_IMAGES } from "../constants/images";

const WHY_ICONS = [ShieldCheck, Building2, Globe2];

export default function Home() {
  const { t } = useLang();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050506]/60 via-[#050506]/75 to-[#050506]" />
          <div className="absolute inset-0 grid-lines opacity-30" />
        </div>
        <div className="absolute top-1/3 -left-20 w-[600px] h-[600px] bg-[#1E90FF]/20 blur-[140px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#00E5FF]/10 blur-[120px] rounded-full" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <div className="max-w-4xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass font-mono text-xs uppercase tracking-[0.18em] text-[#00E5FF]">
              <span className="h-2 w-2 rounded-full bg-[#00E5FF] animate-pulse" />
              {t.hero.badge}
            </span>
            <h1 className="mt-7 text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-[1.02] tracking-tight">
              {t.hero.title}
            </h1>
            <p className="mt-7 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#1E90FF] hover:bg-[#3B82F6] text-white font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-[0_0_30px_rgba(30,144,255,0.4)]"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/global-presence"
                data-testid="hero-cta-global"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 hover:border-white/30 transition-all duration-200"
              >
                {t.hero.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative border-y border-white/8 bg-[#08080a]" data-testid="stats-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {t.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="text-center lg:text-left">
              <div className="font-mono text-4xl md:text-5xl font-bold brand-gradient-text">{s.value}</div>
              <div className="mt-2 text-sm text-zinc-400">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RIBBON */}
      <div className="relative overflow-hidden py-5 bg-[#050506] border-b border-white/8">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center">
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className="mx-8 font-mono text-sm text-zinc-500 uppercase tracking-widest flex items-center gap-8">
                  {t.ribbon}
                  <span className="h-1.5 w-1.5 rounded-full bg-[#1E90FF]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* INTRO */}
      <section className="relative py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <SectionTag>{t.homeIntro.tag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white leading-tight">{t.homeIntro.title}</h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">{t.homeIntro.text}</p>
            <Link to="/about" data-testid="intro-about-link" className="mt-8 inline-flex items-center gap-2 group text-[#1E90FF] font-medium">
              {t.common.learnMore}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative rounded-3xl overflow-hidden border border-white/10">
              <img src={IMAGES.cyber} alt="Technology" className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050506]/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="relative py-24 bg-[#08080a] border-y border-white/8" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <SectionTag>{t.homeServices.tag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">{t.homeServices.title}</h2>
            <p className="mt-4 text-zinc-400 text-lg">{t.homeServices.subtitle}</p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-6 auto-rows-fr">
            {SERVICE_META.map((meta, i) => {
              const Icon = ICONS[meta.icon];
              const svc = t.services.items[meta.key];
              const wide = i === 0 || i === 3;
              return (
                <Reveal key={meta.key} delay={i * 0.06} className={wide ? "md:col-span-2" : ""}>
                  <Link
                    to="/services"
                    data-testid={`home-service-${meta.key}`}
                    className="group block h-full rounded-3xl border border-white/10 bg-[#0F0F12] p-8 transition-all duration-300 hover:border-[#1E90FF]/50 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(30,144,255,0.15)]"
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#1E90FF]/10 border border-[#1E90FF]/20 text-[#1E90FF] group-hover:bg-[#1E90FF]/20 transition-colors">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-white">{svc.name}</h3>
                    <p className="mt-1 font-mono text-xs uppercase tracking-wider text-[#00E5FF]">{svc.tagline}</p>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{svc.description}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDER TEASER */}
      <section className="relative py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal delay={0.05} className="order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-6 bg-[#1E90FF]/15 blur-[70px] rounded-full" />
              <div className="relative rounded-3xl p-[1.5px] bg-gradient-to-br from-[#1E90FF]/70 via-white/10 to-[#00E5FF]/50">
                <div className="rounded-3xl overflow-hidden bg-[#0F0F12]">
                  <div className="relative aspect-square">
                    <img src={IMAGES.founder} alt="Shrikrishna Keskar" className="w-full h-full object-cover object-center" />
                    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10" />
                  </div>
                  <div className="flex items-center justify-between px-6 py-5 border-t border-white/8">
                    <div>
                      <div className="font-display font-bold text-white text-lg">Shrikrishna Keskar</div>
                      <div className="font-mono text-[11px] text-[#00E5FF] mt-1 uppercase tracking-wider">{t.about.storyRole}</div>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-[11px] text-zinc-500">
                      <span className="h-2 w-2 rounded-full bg-[#00E5FF] animate-pulse" /> EST. 2016
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2">
            <SectionTag>{t.homeFounder.tag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white leading-tight">{t.homeFounder.title}</h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">{t.homeFounder.text}</p>
            <Link to="/about" data-testid="founder-story-link" className="mt-8 inline-flex items-center gap-2 group text-[#1E90FF] font-medium">
              {t.homeFounder.cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHY */}
      <section className="relative py-24 bg-[#08080a] border-y border-white/8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <SectionTag>{t.homeWhy.tag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">{t.homeWhy.title}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {t.homeWhy.points.map((p, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="h-full rounded-3xl border border-white/10 bg-[#0F0F12] p-8">
                    <Icon className="h-8 w-8 text-[#00E5FF]" />
                    <h3 className="mt-5 text-lg font-semibold text-white">{p.title}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL TEASER */}
      <section className="relative py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <SectionTag>{t.homeGlobal.tag}</SectionTag>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold text-white">{t.homeGlobal.title}</h2>
            <p className="mt-4 text-zinc-400 text-lg">{t.homeGlobal.subtitle}</p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {t.global.offices.map((o, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <Link to="/global-presence" data-testid={`home-office-${i}`} className="group block rounded-3xl overflow-hidden border border-white/10 relative h-64">
                  <img src={OFFICE_IMAGES[i]} alt={o.city} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050506] via-[#050506]/40 to-transparent" />
                  <div className="absolute bottom-5 left-5">
                    <div className="font-mono text-[11px] uppercase tracking-wider text-[#00E5FF]">{o.type}</div>
                    <div className="mt-1 font-display font-bold text-white text-lg">{o.city}</div>
                    <div className="text-xs text-zinc-400">{o.country}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-gradient-to-br from-[#0d1b2e] to-[#0F0F12] p-12 md:p-16 text-center">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-[#1E90FF]/20 blur-[100px] rounded-full" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold text-white max-w-2xl mx-auto">{t.cta.title}</h2>
                <p className="mt-5 text-zinc-400 text-lg max-w-xl mx-auto">{t.cta.text}</p>
                <Link
                  to="/contact"
                  data-testid="home-cta-contact"
                  className="mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#1E90FF] hover:bg-[#3B82F6] text-white font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-[0_0_30px_rgba(30,144,255,0.4)]"
                >
                  {t.cta.button}
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
